import React from 'react';
import HowItWorksPage from '../components/HowItWorksPage';
import { StatusCards, LifecycleStepper, InfoCallout, UiLabel } from '../components/HelpArticleLayout';

// Suppliers · How it all works — the staff-side explainer for the Suppliers
// area. Written from what the code actually does, not from what the screen
// looks like: the status pill is DERIVED (nobody sets "Active"), the
// onboarding checklist is the real one in lib/onboardingChecklist, and the
// compliance colour is the database's, not this page's.

const ICON = {
  map:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></svg>,
  badge:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6" /><path d="M15.5 12.5 17 22l-5-3-5 3 1.5-9.5" /></svg>,
  steps:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
  shield: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
  key:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3" /></svg>,
  filter: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>,
  chart:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>,
};

const sections = [
  {
    id: 'picture', title: 'What a supplier record is for', icon: ICON.map,
    body: (
      <>
        <p>A supplier record exists so that, on the day you need to send a brief, you can answer three questions without asking anyone: <strong className="text-ink">can we use them</strong>, <strong className="text-ink">are they safe to use</strong>, and <strong className="text-ink">were they any good last time</strong>.</p>
        <p>Everything on the record serves one of those. The list view is the roster; the detail page is the file.</p>
      </>
    ),
  },
  {
    id: 'status', title: 'The status pill is worked out, not chosen', icon: ICON.badge,
    body: (
      <>
        <p>There is no &ldquo;set this supplier to Active&rdquo; control, and that is deliberate. The pill is derived from two facts already on the record:</p>
        <StatusCards items={[
          { color: '#009159', label: 'Active', description: 'Finance has approved them. They can be sent briefs and awarded work.' },
          { color: '#E89A2E', textColor: '#8A5A12', label: 'Onboarding', description: 'On the system but not finance-approved yet. Still being set up.' },
          { color: '#51534A', label: 'Inactive', description: 'Switched off. Hidden from the default list, and their portal login stops working.' },
        ]} />
        <p>So a supplier becomes <strong className="text-ink">Active</strong> the moment finance approval is ticked — not when someone remembers to change a dropdown. It cannot say Active while finance approval is missing, which is the disagreement this replaced.</p>
        <InfoCallout>Deactivating genuinely revokes portal access. It is not a display flag — treat it as switching someone off rather than tidying the list.</InfoCallout>
      </>
    ),
  },
  {
    id: 'onboarding', title: 'The onboarding checklist', icon: ICON.steps,
    body: (
      <>
        <p>Five steps on the supplier&rsquo;s record, ticked as they happen. They are the human half of setting someone up — the part no system can observe:</p>
        <LifecycleStepper
          steps={[
            { color: '#51534A', label: 'Set-up Teams meeting', description: 'The introduction call.' },
            { color: '#51534A', label: 'Documents sent', description: 'The relevant contractor documents have gone out.' },
            { color: '#2E8FA1', label: 'Documents returned', description: 'They have completed and sent them back.' },
            { color: '#2E8FA1', label: 'Escalation information recorded', description: 'Who we ring when something goes wrong on site.' },
            { color: '#009159', label: 'Face-to-face arranged', description: 'The in-person meeting is in the diary.' },
          ]}
        />
        <p>Ticking a step <strong className="text-ink">saves immediately</strong> — there is no Save button to forget. The checklist is a record of conversations, so nothing else in the system reads it: it will not change their status or their compliance colour.</p>
      </>
    ),
  },
  {
    id: 'compliance', title: 'Compliance colour, and why you cannot set it here', icon: ICON.shield,
    body: (
      <>
        <p>Green, amber and red are computed in the database from the documents the supplier owes and the state each one is in. The supplier record <em>shows</em> the answer; it does not decide it.</p>
        <ul className="list-disc pl-5 space-y-1.5 my-1">
          <li><strong className="text-ink">Red</strong> — something required is expired, was rejected, or has never been supplied. <strong className="text-ink">Red suppliers cannot be awarded work.</strong></li>
          <li><strong className="text-ink">Amber</strong> — something expires within 30 days, or is sitting with Fusion awaiting review.</li>
          <li><strong className="text-ink">Green</strong> — everything required is approved and in date.</li>
        </ul>
        <p>Which documents a supplier owes comes from their <strong className="text-ink">risk tier</strong>, set on the record and configured under <UiLabel>Compliance Configuration</UiLabel>. Change the tier and the required set changes with it.</p>
        <InfoCallout tone="info">There is a manual override, used sparingly. Everything else recomputes on its own, so if a colour looks wrong the answer is almost always a document, not the supplier record.</InfoCallout>
      </>
    ),
  },
  {
    id: 'portal', title: 'Portal users — who can actually log in', icon: ICON.key,
    body: (
      <>
        <p>A supplier record is a company. A <strong className="text-ink">portal user</strong> is a person at that company with a login. The two are separate, and a supplier with no active portal user is the most common reason an invitation goes nowhere: nobody can open it.</p>
        <p>That gap is counted on the dashboard and in the daily round-up, because it silently blocks everything downstream — briefs, documents, signatures.</p>
      </>
    ),
  },
  {
    id: 'finding', title: 'Finding people in the list', icon: ICON.filter,
    body: (
      <>
        <p>The list defaults to hiding inactive suppliers — switch the status pill to see them. Beyond that you can narrow by region, risk tier, compliance colour, operational status and tags, and group the results.</p>
        <p>Arriving from Reporting or the dashboard carries the filter with you, and a banner says which drill you came in on. That banner is your cue that the list is <em>not</em> showing everything.</p>
      </>
    ),
  },
  {
    id: 'performance', title: 'Performance and incidents', icon: ICON.chart,
    body: (
      <>
        <p>After an awarded event, four questions are recorded about the supplier: whether they delivered what was agreed, whether they were on time, whether there was a safety concern, and whether we would use them again.</p>
        <p>Those answers roll up on the supplier record into a quality score, an on-time count and a use-again percentage — and they need a few jobs before they mean anything, so a supplier with one job shows their raw answers rather than a rating.</p>
        <InfoCallout tone="warn">A <strong>reportable</strong> safety answer is different in kind. It raises an open safety incident on the supplier and is never averaged away into a score.</InfoCallout>
      </>
    ),
  },
];

export default function SuppliersInfo() {
  return (
    <HowItWorksPage
      sections={sections}
      intro="The supplier roster end to end — what the status pill actually means, how onboarding is tracked, where the compliance colour comes from, and why a supplier with no portal user blocks everything."
      backTo="/suppliers"
      backLabel="Suppliers"
    />
  );
}
